import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormHelperText,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  useColorModeValue,
  Divider,
  Icon,
  Badge,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { FiCheckSquare, FiCheckCircle } from 'react-icons/fi'
import { orderService } from '@/services/admin/orderService'
import { useToast } from '@/hooks/useToast'
import { getChainsByNetwork } from '@/constants/chains'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { DropdownSelect } from '@/components/common/DropdownSelect'

interface ConfirmationResults {
  queuedCount: number
  chainId?: string
}

export function PaymentConfirmationsTrigger() {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const toast = useToast()
  const cardBg = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  const availableChains = getChainsByNetwork(currentEnv).map(chain => ({
    id: String(chain.numericChainId),
    name: chain.shortName || chain.name,
    network: chain.network,
  }))
  
  // Form state
  const [selectedChain, setSelectedChain] = useState('')
  
  // Results state
  const [lastResults, setLastResults] = useState<ConfirmationResults | null>(null)
  
  // Enhanced error categorization helper
  const categorizeError = (error: any) => {
    const message = error.message || error.toString()
    
    if (message.includes('unsupported chain') || message.includes('chain not supported')) {
      return 'unsupportedChain'
    }
    if (message.includes('platform address') || message.includes('FeeTreasury')) {
      return 'platformAddress'
    }
    if (message.includes('configuration') || message.includes('config')) {
      return 'configuration'
    }
    return 'general'
  }

  // Mutation for triggering confirmation updates
  const confirmationMutation = useMutation({
    mutationFn: async () => {
      const params = selectedChain ? { chainId: selectedChain } : {}
      return await orderService.updatePaymentConfirmations(params, currentEnv)
    },
    onSuccess: (data) => {
      setLastResults(data)
      const count = data.queuedCount
      
      // Enhanced success message with chain information
      const chainInfo = data.chainId ? getChainName(data.chainId) : t('monitoring.confirmations.chains.all')
      toast.success({
        title: t('monitoring.confirmations.success.title'),
        description: t('monitoring.confirmations.success.descriptionWithChain', { count, chain: chainInfo }),
        id: 'payment-confirmations-success',
      })
      
      // Reset form on success
      setSelectedChain('')
    },
    onError: (error: any) => {
      const errorType = categorizeError(error)
      let title = t('monitoring.confirmations.error.title')
      let description = error.message || t('monitoring.confirmations.error.description')
      
      // Provide specific error messages based on error type
      switch (errorType) {
        case 'unsupportedChain':
          title = t('monitoring.confirmations.error.unsupportedChain.title')
          description = t('monitoring.confirmations.error.unsupportedChain.description')
          break
        case 'platformAddress':
          title = t('monitoring.confirmations.error.platformAddress.title')
          description = t('monitoring.confirmations.error.platformAddress.description')
          break
        case 'configuration':
          title = t('monitoring.confirmations.error.configuration.title')
          description = t('monitoring.confirmations.error.configuration.description')
          break
        default:
          title = t('monitoring.confirmations.error.title')
          description = error.message || t('monitoring.confirmations.error.description')
      }
      
      toast.error({
        title,
        description,
        id: 'payment-confirmations-error',
      })
    },
  })
  
  const handleSubmit = () => {
    confirmationMutation.mutate()
  }
  
  const getChainName = (chainId: string) => {
    return availableChains.find(chain => chain.id === chainId)?.name || chainId
  }

  return (
    <Card bg={cardBg} borderColor={borderColor}>
      <CardHeader>
        <HStack justify="space-between">
          <HStack>
            <Icon as={FiCheckSquare} color="green.500" />
            <Heading size="md">{t('monitoring.confirmations.title')}</Heading>
          </HStack>
          <Badge colorScheme="green" variant="subtle">
            {t('monitoring.confirmations.badge')}
          </Badge>
        </HStack>
      </CardHeader>
      
      <CardBody>
        <VStack spacing={6} align="stretch">
          {/* Description */}
          <Alert status="info" variant="left-accent" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="sm">{t('monitoring.confirmations.info.title')}</AlertTitle>
              <AlertDescription fontSize="xs">
                {t('monitoring.confirmations.info.description')}
              </AlertDescription>
            </Box>
          </Alert>
    
          {/* Form Fields */}
          <VStack spacing={4} align="stretch">
            {/* Chain Selection */}
            <FormControl>
              <FormLabel fontSize="sm">{t('monitoring.confirmations.fields.chain.label')}</FormLabel>
              <DropdownSelect
                placeholder={t('monitoring.confirmations.fields.chain.placeholder')}
                emptyOptionLabel={t('monitoring.confirmations.fields.chain.placeholder')}
                value={selectedChain}
                options={availableChains.map(chain => ({
                  value: chain.id,
                  label: chain.name,
                }))}
                onChange={setSelectedChain}
                ariaLabel={t('monitoring.confirmations.fields.chain.label')}
                size="sm"
                isDisabled={confirmationMutation.isPending}
              />
              <FormHelperText fontSize="xs">
                {t('monitoring.confirmations.fields.chain.helper')}
              </FormHelperText>
              
              {/* Chain Support Information */}
                <VStack spacing={2} align="stretch" mt={3}>
                  <Heading size="xs">{t('monitoring.confirmations.fields.chain.supportedChains')}</Heading>
                  <VStack align="start" spacing={1}>
                  <Heading size="xs" color="blue.500">
                    {t(`monitoring.confirmations.fields.chain.${currentEnv}`)}
                  </Heading>
                  <HStack spacing={2} flexWrap="wrap">
                    {availableChains.map(chain => (
                      <Badge key={chain.id} colorScheme="blue" variant="subtle" size="sm">
                        {chain.name}
                      </Badge>
                    ))}
                  </HStack>
                </VStack>
              </VStack>
            </FormControl>
          </VStack>
          
          {/* Submit Button */}
          <Button
            colorScheme="green"
            onClick={handleSubmit}
            isLoading={confirmationMutation.isPending}
            loadingText={t('monitoring.confirmations.button.loading')}
            leftIcon={<FiCheckSquare />}
            size="md"
          >
            {t('monitoring.confirmations.button.submit')}
          </Button>
          
          {/* Results Display */}
          {lastResults && (
            <>
              <Divider />
              <VStack spacing={4} align="stretch">
                <HStack>
                  <Icon as={FiCheckCircle} color="green.500" />
                  <Heading size="sm">{t('monitoring.confirmations.results.title')}</Heading>
                </HStack>
                
                <Stat>
                  <StatLabel fontSize="xs">
                    {t('monitoring.confirmations.results.updated')}
                  </StatLabel>
                  <StatNumber fontSize="2xl" color="green.500">
                    {lastResults.queuedCount}
                  </StatNumber>
                </Stat>
                
                {/* Chain Processing Info */}
                {lastResults.chainId && (
                  <Alert status="info" variant="left-accent" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle fontSize="sm">
                        {t('monitoring.confirmations.results.chainProcessing')}
                      </AlertTitle>
                      <AlertDescription fontSize="xs">
                        {t('monitoring.confirmations.results.chainInfo', { 
                          chain: getChainName(lastResults.chainId) 
                        })}
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}

                <Alert status="success" variant="left-accent" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription fontSize="xs">
                    {t('monitoring.confirmations.results.successMessage')}
                  </AlertDescription>
                </Alert>
              </VStack>
            </>
          )}
        </VStack>
      </CardBody>
    </Card>
  )
}
